import forEach from 'lodash/forEach';
import has from 'lodash/has';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import every from 'lodash/every';
import * as actionTypes from '../../constants/actionTypes';
import DataSamplesProcessor from '../../lib/DataSamplesProcessor';
import { DATA_CHARS_ENABLED } from '../../constants/config';

import { fetchDataSamples, fetchCharacteristics, fetchSamplesForTransform } from '../dataSets';

export const initSchemaEditor = (dataSetId = null, inputSchema, outputSchema) => {
  return {
    type: actionTypes.SCHEMA_EDITOR_INIT,
    payload: {
      dataSetId,
      inputSchema,
      outputSchema,
    },
  };
};

export const initSchemaEditorOptions = () => {
  return {
    type: actionTypes.SCHEMA_EDITOR_INIT_OPTIONS,
  };
};

export const updateInputSchema = (inputSchema) => {
  return {
    type: actionTypes.SCHEMA_EDITOR_UPDATE_INPUT_SCHEMA,
    payload: {
      inputSchema,
    },
  };
};

export const resetSchemaEditor = () => {
  return {
    type: actionTypes.SCHEMA_EDITOR_RESET,
    payload: null,
  };
};

export const clearSchemaEditorError = () => {
  return {
    type: actionTypes.SCHEMA_EDITOR_CLEAR_ERROR,
  };
};

export const updateSchemaEditorSamples = ({
  dataSetId,
  fromNested,
  fromFlat,
  toNested,
  toFlat,
  toRaw,
  fromUpdatedAt,
  toUpdatedAt,
}) => {
  return {
    type: actionTypes.SCHEMA_EDITOR_UPDATE_SAMPLES,
    payload: {
      dataSetId,
      fromUpdatedAt,
      fromNested,
      fromFlat,
      toUpdatedAt,
      toNested,
      toFlat,
      toRaw,
    },
  };
};

export const updateSchemaEditorOutputSamples = ({
  dataSetId,
  toNested,
  toFlat,
  toRaw,
  schemas,
  transform,
  error = null,
  isEmptyOutput = false,
  toUpdatedAt,
}) => {
  return {
    type: actionTypes.SCHEMA_EDITOR_UPDATE_OUTPUT_SAMPLES,
    payload: {
      dataSetId,
      toNested,
      toFlat,
      toRaw,
      schemas,
      transform,
      error,
      isEmptyOutput,
      toUpdatedAt,
    },
  };
};

export const fetchTransformedSamplesFromEditor =
  (txObject, saObject = null, transformId, outputValidations, inSamples, dataSetId) =>
  (dispatch) => {
    return dispatch(
      fetchSamplesForTransform(
        txObject,
        saObject,
        transformId,
        outputValidations,
        inSamples,
        dataSetId,
      ),
    ).then(
      (response) => {
        const {
          result: { output, schemas, status, transform },
          receivedAt,
        } = response;
        if (status === 'ok') {
          let toNested = [],
            toFlat = [];
          let hasError = false,
            error = null;
          forEach(output, (s) => {
            toNested.push(DataSamplesProcessor.rorderSampleProperties(s));
            toFlat.push(DataSamplesProcessor.flattenSamples(s));
            if (has(s, 'error') && has(s, 'rawMessage') && s.error) {
              hasError = true;
              error = s.error;
            }
          });

          const isAllNullOutput =
            Array.isArray(output) && output.length > 0 && every(output, isNil);
          const isAllRawMessageNull =
            Array.isArray(output) &&
            output.length > 0 &&
            every(output, (item) => {
              return item === null || item === undefined || (item && isNil(item.rawMessage));
            });

          const isEmptyOutput =
            isEmpty(output) ||
            isAllNullOutput ||
            isAllRawMessageNull ||
            isEmpty(schemas) ||
            isEmpty(get(schemas, '[0].properties'));

          dispatch(
            updateSchemaEditorOutputSamples({
              dataSetId,
              toNested,
              toFlat,
              toRaw: output,
              schemas,
              transform,
              error: hasError === true && error ? error : null,
              isEmptyOutput,
              toUpdatedAt: receivedAt,
            }),
          );
        } else {
          console.log('tx status != ok');
          dispatch(
            updateSchemaEditorOutputSamples({
              toNested: null,
              toFlat: null,
              toRaw: null,
              schemas: null,
              transform: null,
              error: { message: 'Transform error' },
              isEmptyOutput: false,
              toUpdatedAt: receivedAt,
            }),
          );
        }
      },
      (error) => {
        console.log('tx error', error);
        let receivedAt = Date.now();
        dispatch(
          updateSchemaEditorOutputSamples({
            dataSetId,
            toNested: null,
            toFlat: null,
            toRaw: null,
            schemas: null,
            transform: null,
            error: error,
            isEmptyOutput: false,
            toUpdatedAt: receivedAt,
          }),
        );
      },
    );
  };

export const fetchDataSamplesFromEditor =
  (
    numOfSamples,
    live,
    dataSetId,
    txObject = null,
    saObject = null,
    transformId = 0,
    outputValidations = [],
    isEditMode = false,
  ) =>
  (dispatch) => {
    return dispatch(fetchDataSamples(dataSetId, numOfSamples, live)).then(
      (response) => {
        const { receivedAt, result } = response;
        let fromNested = [],
          fromFlat = [],
          toNested = [],
          toFlat = [],
          toRaw = [];
        forEach(result, (s) => {
          if (isEditMode === false) {
            fromNested.push(DataSamplesProcessor.rorderSampleProperties(s));
            fromFlat.push(DataSamplesProcessor.flattenSamples(s));
          } else {
            // in edit mode, fetched both input and output samples
            fromNested.push(DataSamplesProcessor.rorderSampleProperties(get(s, 'input', s)));
            fromFlat.push(DataSamplesProcessor.flattenSamples(get(s, 'input', s)));
            if (!txObject) {
              toNested.push(DataSamplesProcessor.rorderSampleProperties(s.output));
              toFlat.push(DataSamplesProcessor.flattenSamples(get(s, 'output', s)));
            }
          }
        });
        dispatch(
          updateSchemaEditorSamples({
            dataSetId,
            fromNested,
            fromFlat,
            toNested,
            toFlat,
            toRaw,
            fromUpdatedAt: receivedAt,
            toUpdatedAt: receivedAt,
          }),
        );

        if (DATA_CHARS_ENABLED) {
          dispatch(fetchCharacteristics(dataSetId));
        }

        if (txObject || transformId) {
          dispatch(
            fetchTransformedSamplesFromEditor(
              txObject,
              saObject,
              transformId,
              outputValidations,
              fromNested,
              dataSetId,
            ),
          );
        }
      },
      (error) => {
        let receivedAt = Date.now();
        dispatch(
          updateSchemaEditorSamples({
            dataSetId,
            fromNested: null,
            fromFlat: null,
            toNested: null,
            toFlat: null,
            fromUpdatedAt: receivedAt,
            toUpdatedAt: receivedAt,
          }),
        );
      },
    );
  };
